// Joining private worlds by access code.
//
// RLS hides a private world from everyone but its owner and members, so the
// code can't be looked up from the client. The `join_world_with_code` RPC does
// the lookup server-side, adds the caller to world_access as a viewer, and
// rate-limits wrong guesses (10 per hour). After joining, the normal worlds
// query works for that user without the code.
import { supabase } from '../config/supabase';

/** Strip spaces/dashes and uppercase, the same way the server does. */
export const normalizeAccessCode = (code: string) => code.replace(/[^a-z0-9]/gi, '').toUpperCase();

/** Join a private world. Returns its id, or throws an Error with a message fit to show. */
export async function joinWorldWithCode(code: string, worldId?: string): Promise<string> {
  const clean = normalizeAccessCode(code);
  if (!clean) throw new Error('Enter the world’s access code.');
  const { data, error } = await supabase.rpc('join_world_with_code', {
    p_code: clean,
    p_world: worldId ?? null,
  });
  if (error) {
    const hint = (error as { hint?: string }).hint;
    if (hint === 'rate_limited' || hint === 'auth_required' || hint === 'bad_input') throw new Error(error.message);
    if (/JWT|permission denied/i.test(error.message)) throw new Error('Sign in to join a private world.');
    throw new Error('Couldn’t check that code. Please try again.');
  }
  if (!data) throw new Error('That code doesn’t match. Check it with the world’s owner.');
  return data as string;
}

export type WorldAccessState = 'ok' | 'private' | 'missing';

/** Why a world couldn't be read: it's private to the caller, or it doesn't exist. */
export async function getWorldAccessState(worldId: string): Promise<WorldAccessState | null> {
  const { data, error } = await supabase.rpc('world_access_state', { p_world: worldId });
  if (error) return null;
  return data as WorldAccessState;
}
